import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import sessionManagementService from '../../services/sessionManagementService';
import { supabase } from '../../lib/supabase';
import { Monitor, Bell, Shield, FileText, Key } from 'lucide-react';


import { toast } from '../../components/ui/Toast';
import NavigationSidebar from '../../components/NavigationSidebar';
import SessionTimeoutControls from './components/SessionTimeoutControls';
import ActiveDevicesList from './components/ActiveDevicesList';
import AuditLogDashboard from './components/AuditLogDashboard';
import SecurityAlertsDashboard from './components/SecurityAlertsDashboard';
import PatternDetectionDashboard from './components/PatternDetectionDashboard';
import TwoFactorSetup from './components/TwoFactorSetup';

const SecuritySettings = () => {
  const { user, securityPreferences, setSecurityPreferences, sessionToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [activeSessions, setActiveSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [activeTab, setActiveTab] = useState('sessions'); // 'sessions', 'alerts', 'patterns', 'audit', '2fa'

  useEffect(() => {
    if (user?.id) {
      loadPreferences();
      loadActiveSessions();

      // Subscribe to real-time changes for security preferences
      const channel = supabase
        ?.channel(`security-preferences-${user?.id}`)
        ?.on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'user_profiles',
            filter: `id=eq.${user?.id}`,
          },
          (payload) => {
            const updatedPreferences = payload?.new?.security_preferences;
            if (updatedPreferences) {
              setPreferences(updatedPreferences);
              setSecurityPreferences(updatedPreferences);
              toast?.info('Security settings updated', {
                description: 'Settings synced from another session',
              });
            }
          }
        )
        ?.subscribe();

      // Subscribe to active_sessions changes
      const sessionsChannel = supabase
        ?.channel(`active-sessions-${user?.id}`)
        ?.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'active_sessions',
            filter: `user_id=eq.${user?.id}`,
          },
          () => {
            loadActiveSessions();
          }
        )
        ?.subscribe();

      return () => {
        supabase?.removeChannel(channel);
        supabase?.removeChannel(sessionsChannel);
      };
    } else {
      setLoading(false);
    }
  }, [user?.id]);

  const loadPreferences = async () => {
    setLoading(true);
    try {
      const { data, error } = await sessionManagementService?.getSecurityPreferences(user?.id);
      if (error) throw error;

      const defaultPrefs = sessionManagementService?.getDefaultSecurityPreferences();
      setPreferences(data && Object.keys(data)?.length > 0 ? data : defaultPrefs);
    } catch (error) {
      console.error('Error loading security preferences:', error);
      toast?.error('Failed to load security settings');
      setPreferences(sessionManagementService?.getDefaultSecurityPreferences());
    } finally {
      setLoading(false);
    }
  };

  const loadActiveSessions = async () => {
    setLoadingSessions(true);
    try {
      const { data, error } = await sessionManagementService?.getActiveSessions();
      if (error) throw error;
      setActiveSessions(data || []);
    } catch (error) {
      console.error('Error loading active sessions:', error);
      toast?.error('Failed to load active sessions');
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await sessionManagementService?.updateSecurityPreferences(user?.id, preferences);
      if (error) throw error;

      setSecurityPreferences(preferences);
      toast?.success('Security settings saved successfully', {
        description: 'Your settings have been updated',
      });
    } catch (error) {
      console.error('Error saving security preferences:', error);
      toast?.error('Failed to save security settings');
    } finally {
      setSaving(false);
    }
  };

  const updatePreferences = (section, updates) => {
    setPreferences((prev) => ({
      ...prev,
      [section]: {
        ...prev?.[section],
        ...updates,
      },
    }));
  };

  const handleLogoutAllDevices = async () => {
    if (!sessionToken) {
      toast?.error('Unable to logout all devices', {
        description: 'Session token not found',
      });
      return;
    }

    try {
      const { data, error } = await sessionManagementService?.logoutAllDevices(sessionToken);
      if (error) throw error;

      toast?.success('Logged out from all other devices', {
        description: `${data || 0} session(s) terminated`,
      });
      loadActiveSessions();
    } catch (error) {
      console.error('Error logging out all devices:', error);
      toast?.error('Failed to logout all devices');
    }
  };

  const handleTerminateSession = async (sessionId) => {
    try {
      const { error } = await sessionManagementService?.terminateSession(sessionId);
      if (error) throw error;

      toast?.success('Session terminated successfully');
      loadActiveSessions();
    } catch (error) {
      console.error('Error terminating session:', error);
      toast?.error('Failed to terminate session');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-muted animate-pulse"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      <NavigationSidebar />
      <div className="flex-1 ml-0 lg:ml-64">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center p-2">
                <img 
                  src="/assets/images/Photo_Feb_21_2026_12_00_04_AM_1_-1771799585955.png" 
                  alt="DraftIQ Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground font-heading">
                  Security Settings
                </h1>
                <p className="text-muted-foreground">
                  Manage session timeouts, token refresh intervals, and active devices
                </p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex px-6 overflow-x-auto">
              <button
                onClick={() => setActiveTab('sessions')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'sessions' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <Monitor className="w-4 h-4 inline mr-1" />
                Active Sessions
              </button>
              <button
                onClick={() => setActiveTab('alerts')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'alerts' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <Bell className="w-4 h-4 inline mr-1" />
                Security Alerts
              </button>
              <button
                onClick={() => setActiveTab('patterns')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'patterns' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <Shield className="w-4 h-4 inline mr-1" />
                Pattern Detection
              </button>
              <button
                onClick={() => setActiveTab('audit')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'audit' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-1" />
                Audit Logs
              </button>
              <button
                onClick={() => setActiveTab('2fa')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === '2fa' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <Key className="w-4 h-4 inline mr-1" />
                Two-Factor Auth
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'sessions' && (
              <div className="space-y-6">
                <ActiveDevicesList 
                  sessions={activeSessions}
                  loading={loadingSessions}
                  currentSessionToken={sessionToken}
                  onTerminateSession={handleTerminateSession}
                  onLogoutAllDevices={handleLogoutAllDevices}
                  onRefresh={loadActiveSessions}
                />
                <SessionTimeoutControls 
                  preferences={preferences}
                  onUpdate={(updates) => updatePreferences('sessionTimeout', updates)}
                />
              </div>
            )}

            {activeTab === 'alerts' && <SecurityAlertsDashboard />}

            {activeTab === 'patterns' && <PatternDetectionDashboard />}

            {activeTab === 'audit' && <AuditLogDashboard />}

            {activeTab === '2fa' && <TwoFactorSetup />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;